import React, { useState, useEffect } from 'react';
import { Form, Input, Button, message, Row, Col, Select, Upload, Avatar, Space, Popconfirm } from 'antd';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import { updateUser, uploadProfileFile, deleteProfilePicture } from '../ProfileServices';
import { getBackendMessageOr } from '../../../Utils/apiMessages';
import { DeleteOutlined, EditOutlined, CameraOutlined } from '@ant-design/icons';
import { useGlobal } from '../../Shared/GlobalContext/GlobalContext';
import ImgCrop from 'antd-img-crop';
import { getFileUploadPolicy } from '../../../Utils/fileUploadPolicy';
import { LANGUAGE_OPTIONS } from '../../../Constants/language';
import { useNativeFileUpload } from '../../../Hooks/useNativeFileUpload';
import { shouldUseNativePicker } from '../../../Utils/nativeFileUpload';
import { useFileUploadPolicy } from '../../../Hooks/useFileUploadPolicy';
import { setupNativeUploadIntegration } from '../../../Utils/nativeUploadIntegration';
import ResponsiveModal from '../../Shared/ResponsiveModal/ResponsiveModal';

const languageOptions = LANGUAGE_OPTIONS;

const timezones = moment.tz.names().map((timezone) => ({
    value: timezone,
    label: timezone,
}));

// We rely on backend-provided timezone_name for display

const EditProfile = ({ handleClose, user, fetchUser }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const { loading, setLoading } = useGlobal();
    const [photoUrl, setPhotoUrl] = useState(user?.picture_url || undefined);
    const [photoUploadId, setPhotoUploadId] = useState(null);
    const [deletingPhoto, setDeletingPhoto] = useState(false);
    const { policy } = useFileUploadPolicy();
    const isNative = shouldUseNativePicker();



    // Initialize native file upload hook
    const { uploading: uploadingPhoto, handleNativeImagePick, beforeUpload } = useNativeFileUpload({
        uploadFunction: uploadProfileFile,
        policy,
        imagesOnly: true,
        onSuccess: (processedFile, uploadResponse) => {
            const url = uploadResponse?.data?.file_url;
            if (url) {
                setPhotoUrl(url);
                setPhotoUploadId(uploadResponse?.data?.id || null);
                // Use backend message_code if available, fallback to upload_success
                const successMessage = getBackendMessageOr(t, uploadResponse, 'upload_success');
                if (successMessage) {
                    message.success(successMessage);
                }
            }
        },
        // onError removed - hook will use handleError for API errors with message_code support
    });



    // Use backend-provided timezone_name for display
    const initialTimezone = user?.timezone_name || undefined;
    const initialLanguage = (user?.language || user?.locale || '') ? (user?.language || user?.locale).split('-')[0] : undefined;

    // Setup native upload integration on mobile to make ImgCrop work with native camera
    useEffect(() => {
        if (isNative) {
            const cleanup = setupNativeUploadIntegration();
            return cleanup;
        }
    }, [isNative]);

    const handleSubmit = async () => {
        try {
            setLoading(true);
            const values = await form.validateFields();

            let updatedValues = { ...values };

            // Persist the selected timezone string as timezone_name; compute offset for compatibility
            if (values.timezone) {
                const timezone = values.timezone;
                const offset = moment.tz(timezone).utcOffset() / 60;
                updatedValues = {
                    ...values,
                    timezone: offset,
                    timezone_name: timezone,
                };
            }

            if (photoUploadId) {
                updatedValues.profile_picture_file_upload_id = photoUploadId;
            }

            // Remove null/undefined fields to avoid sending invalid values (e.g., locale: null)
            const filteredValues = Object.fromEntries(
                Object.entries(updatedValues).filter(([, v]) => v !== null && v !== undefined)
            );

            const payload = await updateUser(filteredValues);
            fetchUser();
            handleClose();
            // Optional: surface success notice if backend provided one
            const successText = getBackendMessageOr(t, payload, '');
            if (successText) {
                message.success(successText);
            }
        } catch (error) {
            message.error(t('error_updating_profile'));
        } finally {
            setLoading(false);
        }
    };
    return (
        <ResponsiveModal
            title={t('edit_profile')}
            footer={
                < Button size="medium" type="primary" block loading={loading}
                    onClick={handleSubmit} >
                    {t('save_changes')}
                </Button >
            }
            open
            onCancel={handleClose}
            desktopWidth={600}
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={{
                    first_name: user?.first_name,
                    last_name: user?.last_name,
                    email: user?.email,
                    gender: user?.gender,
                    language: initialLanguage,
                    timezone: initialTimezone,
                }}
            >
                <Row style={{ marginBottom: 16 }}>
                    <Col span={24}>
                        <Space align="center" size={16}>
                            <Avatar size={64} src={photoUrl} data-file-upload-id={photoUploadId || undefined}>
                                {!photoUrl && `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`}
                            </Avatar>


                            {/* Both web and native: Use ImgCrop for consistent cropping experience */}
                            <ImgCrop
                                rotationSlider
                                zoomSlider
                                aspect={1}
                                quality={0.9}
                                modalTitle={t('edit_photo')}
                                shape="round"
                            >
                                <Upload
                                    accept="image/*"
                                    showUploadList={false}
                                    beforeUpload={beforeUpload}
                                >
                                    <Button
                                        shape="circle"
                                        icon={isNative ? <CameraOutlined /> : <EditOutlined />}
                                        loading={uploadingPhoto}
                                        aria-label={t('change_photo')}
                                    />
                                </Upload>
                            </ImgCrop>

                            {photoUrl && (
                                <Popconfirm
                                    title={t('delete_profile_picture')}
                                    onConfirm={async () => {
                                        try {
                                            setDeletingPhoto(true);
                                            const payload = await deleteProfilePicture();
                                            setPhotoUrl(undefined);
                                            setPhotoUploadId(null);
                                            message.success(getBackendMessageOr(t, payload, 'profile_picture_deleted_successfully'));
                                            if (typeof fetchUser === 'function') {
                                                await fetchUser();
                                            }
                                        } catch (e) {
                                            message.error(t('failed_to_delete_profile_picture'));
                                        } finally {
                                            setDeletingPhoto(false);
                                        }
                                    }}
                                    okText={t('yes')}
                                    cancelText={t('no')}
                                >
                                    <Button danger shape="circle" loading={deletingPhoto} icon={<DeleteOutlined />} aria-label={t('delete_profile_picture')} />
                                </Popconfirm>
                            )}
                        </Space>
                    </Col>
                </Row>
                <Row gutter={[24, 0]} style={{ margin: 0 }}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="first_name"
                            label={t('first_name')}
                            rules={[{ required: true, message: t('first_name_is_required') }]}
                        >
                            <Input size="medium" />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                        <Form.Item
                            name="last_name"
                            label={t('last_name')}
                            rules={[{ required: true, message: t('last_name_is_required') }]}
                        >
                            <Input size="medium" />
                        </Form.Item>
                    </Col>

                    <Col md={12} xs={24}>
                        <Form.Item
                            name="email"
                            label={t('email_address')}
                            rules={[
                                { required: true, message: t('email_is_required') },
                                { type: 'email', message: t('please_enter_a_valid_email') }
                            ]}
                        >
                            <Input size="medium" />
                        </Form.Item>
                    </Col>

                    <Col md={12} xs={24}>
                        <Form.Item
                            name="gender"
                            label={t('gender')}
                            rules={[{ required: true, message: t('gender_is_required') }]}
                        >
                            <Select
                                options={[
                                    { value: 'male', label: t('male') },
                                    { value: 'female', label: t('female') }
                                ]}
                                showSearch={false}
                                size="medium"
                            />
                        </Form.Item>
                    </Col>

                    <Col md={12} xs={24}>
                        <Form.Item
                            name="language"
                            label={t('language')}
                            rules={[{ required: true, message: t('language_is_required') }]}
                            style={{ width: '100%' }}
                        >
                            <Select
                                options={languageOptions}
                                showSearch={false}
                                size="medium"
                            />
                        </Form.Item>
                    </Col>

                    <Col md={12} xs={24}>
                        <Form.Item
                            name="timezone"
                            label={t('timezone')}
                            rules={[{ required: true, message: t('timezone_is_required') }]}
                            style={{ width: '100%' }}
                        >
                            <Select
                                options={timezones}
                                showSearch={false}
                                size="medium"
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </ResponsiveModal >
    );
};

export default EditProfile;
